/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  IconButton,
  Collapse,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { NitroMarkerBase } from "../../../components/map/components/NitroMarker";
import { toCamelCase } from "../../../utils/String";
import TruckIconFO from "../../../icons/TruckIconFO";
import RailIconFO from "../../../icons/RailIconFO";
import { FreightTruck, IQuotesByDestination } from "../../../types/IQuotes";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { OriginTable } from "./OriginTable";
import {
  getQuoteRowKey,
  isRailFreightQuotedOrNotified,
  resolveNetback,
  sortDestinations,
  sortQuotesByNetbackAndDlvd,
} from "./multiLocationQuoteUtils";
import { QuotedRateIcon } from "../../../icons/QuotedRateIcon";
import { QuotedRateNotifiedIcon } from "../../../icons/QuotedRateNotifiedIcon";
import { PricePreference } from "./KochDlvdOfferToggle";
import { resolveDisplayForQuote } from "./pricingDisplay";

// TODO: reduce resued code to  reusable functions or components
// retihink surrounding since now they load later. how does auto scroll work?
// might be hard for this one since we need to filter out city pins for this one
interface TableProps {
  surroundingQuotes: IQuotesByDestination[];
  kochPricePreference: PricePreference;
  filterText?: string;
}

export const SurroundingLocationsTable: React.FC<TableProps> = ({
  surroundingQuotes,
  kochPricePreference,
  filterText,
}) => {
  const [
    openInsightsPanel,
    setSelectedLocation,
    setPanToCoordinates,
    setTempPinLocation,
    multiLocationDestinations,
    product,
    setShowHidePinsPopup,
    currentFilters,
  ] = useNitroStore((state) => [
    state.openInsightsPanel,
    state.setSelectedLocation,
    state.setPanToCoordinates,
    state.setTempPinLocation,
    state.multiLocationDestinations,
    state.product,
    state.setShowHidePinsPopup,
    state.currentFilters,
  ]);
  const [
    setDisplayFreightDetailsPanel,
    setTroubleshootQuoteOpen,
    triggerReset,
    expandedDestination,
    hiddenOrigins,
    setSelectedTruckDetailRecord,
    setDisplayTruckFreightDetailsPanel,
    setAnchorForTruckDetailsPanel,
    setDisplayInboundModeTogglePanel,
    resetExpandRailOriginsWithNoFreight,
    setExpandRailOriginsWithNoFreightTrigger,
    resetExpandTruckOriginsWithNoFreight,
    setExpandTruckOriginsWithNoFreightTrigger,
    setSurroundingQuoteOpen,
    closeSurroundingQuote,
    destinationQuoteOpen,
    triggerCloseDestinationQuote,
    expandedDestId,
    setExpandedDestId,
    setExpandedDestination,
    setRailFreightDetailContext,
    railTeamNotifiedRows,
  ] = useNitroCAStore((state) => [
    state.setDisplayRailFreightDetailsPanel,
    state.setTroubleshootQuoteOpen,
    state.triggerReset,
    state.expandedDestination,
    state.hiddenOrigins,
    state.setSelectedTruckDetailRecord,
    state.setDisplayTruckFreightDetailsPanel,
    state.setAnchorForTruckDetailsPanel,
    state.setDisplayInboundModeTogglePanel,
    state.resetExpandRailOriginsWithNoFreight,
    state.setExpandRailOriginsWithNoFreightTrigger,
    state.resetExpandTruckOriginsWithNoFreight,
    state.setExpandTruckOriginsWithNoFreightTrigger,
    state.setSurroundingQuoteOpen,
    state.closeSurroundingQuote,
    state.destinationQuoteOpen,
    state.triggerCloseDestinationQuote,
    state.expandedDestId,
    state.setExpandedDestId,
    state.setExpandedDestination,
    state.setRailFreightDetailContext,
    state.railTeamNotifiedRows,
  ]);
  const refetchQuotes = useNitroCAStore((state) => state.refetchQuotes);
  const [hoveringDestination, setHoveringDestination] = useState({
    isHovering: false,
    id: "",
  });
  const [autoScroll, setAutoScroll] = useState(false);
  const autoScrollRef = useRef<HTMLElement | null>(null);
  const queryType =
    currentFilters.city || currentFilters.location ? "location" : "party";

  // generic function that handles behavior for expanding/collapsing destination rows whether from button click or external trigger
  const handleToggleExpand = (dest: { id: string; name: string }) => {
    // always perform these actions
    setDisplayInboundModeTogglePanel(false);

    const isCurrentlyExpanded = expandedDestId === dest.id;
    setExpandedDestId(isCurrentlyExpanded ? undefined : dest.id);

    // only perform these actions when collapsing
    if (isCurrentlyExpanded) {
      setSelectedLocation(undefined);
      setTempPinLocation(undefined);
      setSurroundingQuoteOpen(false);
      setShowHidePinsPopup(true);
      return;
    }

    // only perform these actions when expanding
    const destinationRecord = multiLocationDestinations?.find(
      (record: any) =>
        record.destination?.id === dest.id ||
        record.destination?.name === dest.name,
    );

    if (destinationRecord?.destination) {
      const locationRecord = {
        id: destinationRecord.destination.id,
        name: destinationRecord.destination.name,
        owner: destinationRecord.destination.owner || "",
        address: "",
        city: destinationRecord.destination.city || "",
        state: "",
        zip: "",
        country: "",
        price: null,
        lat: destinationRecord.destination.coordinate?.[0] || 0,
        long: destinationRecord.destination.coordinate?.[1] || 0,
        products: [],
        modes: [],
        carriers: [],
        pinType: "customer" as const,
      };

      if (destinationQuoteOpen) {
        triggerCloseDestinationQuote();
      }
      setSelectedLocation(locationRecord);
      setPanToCoordinates({
        lat: locationRecord.lat,
        lng: locationRecord.long,
        zoom: 0,
      });
      setShowHidePinsPopup(false);
      setSurroundingQuoteOpen(true);
      setDisplayFreightDetailsPanel(false);
      setTroubleshootQuoteOpen(false);
      triggerReset();
    }
  };

  // specific function for handling button click to expand/collapse destination rows
  const handleToggleClick = (dest: { id: string; name: string }) => {
    // when user clicks the button we want to reset any expansions set to display
    resetExpandTruckOriginsWithNoFreight();
    resetExpandRailOriginsWithNoFreight();
    setAutoScroll(false);
    const isCurrentlyExpanded = expandedDestId === dest.id;
    if (isCurrentlyExpanded) {
      setExpandedDestination(undefined);
    }
    handleToggleExpand(dest);
  };

  // specific effect for handling external trigger to expand/collapse destination rows
  // currently only use cases are opening truck or rail with no freight separately
  useEffect(() => {
    if (expandedDestination) {
      if (expandedDestination.expandSurroundingLocations) {
        return;
      }

      setAutoScroll(true);
      // if the expand rail with no freight flag is on then trigger that expansion
      if (expandedDestination.expandRailWithNoFreight) {
        setExpandRailOriginsWithNoFreightTrigger();
      }
      // if the expand truck with no freight flag is on then trigger that expansion
      if (expandedDestination.expandTruckWithNoFreight) {
        setExpandTruckOriginsWithNoFreightTrigger();
      }
      handleToggleExpand(expandedDestination);
    }
  }, [expandedDestination]);

  // filter quotes
  const { filteredQuotes } = useMemo(() => {
    // applying mode filters
    const filteredByModes = surroundingQuotes.map((entry) => {
      const filteredQuotes = entry.quotes.filter((quote) => {
        const mode = quote.mode.toLowerCase();
        const originKey = `${quote.competitor ? "competitor" : "koch"}-${
          quote.origin?.name
        }`;
        return !hiddenOrigins[mode as "truck" | "rail"].has(originKey);
      });
      return {
        destination: entry.destination,
        quotes: filteredQuotes,
      };
    });

    const filteredByNonCityPins = filteredByModes.filter(
      (entry) => !entry.destination.isCity,
    );
    if (!filterText) {
      return {
        filteredQuotes: sortDestinations(
          filteredByNonCityPins,
          queryType,
        ) as IQuotesByDestination[],
      };
    }

    // applying text filter
    return {
      filteredQuotes: sortDestinations(
        filteredByNonCityPins.filter(
          (entry) =>
            entry.destination.displayName
              .toLowerCase()
              .includes(filterText.toLowerCase()) ||
            entry.destination.city
              ?.toLowerCase()
              .includes(filterText.toLowerCase()) ||
            entry.destination.stateOrProvince
              ?.toLowerCase()
              .includes(filterText.toLowerCase()),
        ),
        queryType,
      ) as IQuotesByDestination[],
    };
  }, [hiddenOrigins, product, filterText, surroundingQuotes]);

  // close panel effect
  useEffect(() => {
    if (closeSurroundingQuote <= 0) {
      return;
    }

    setExpandedDestId(undefined);
    setSurroundingQuoteOpen(false);
  }, [closeSurroundingQuote]);

  return (
    <Box>
      <TableContainer style={{ overflow: "visible" }}>
        <Table stickyHeader>
          <TableHead className={styles.destinationTableHead}>
            <TableRow>
              <TableCell className={styles.headerCell}>Destination</TableCell>
              <TableCell className={styles.headerCell}>
                Competitor
                <br />
                <span style={{ whiteSpace: "nowrap" }}>DLVD Offer</span>
              </TableCell>
              <TableCell className={styles.headerCell}>
                Koch
                <br />
                <span style={{ whiteSpace: "nowrap" }}>DLVD Offer</span>
              </TableCell>
              <TableCell
                className={styles.headerCell}
                style={{ paddingLeft: "7px" }}
              >
                Freight
              </TableCell>
              <TableCell className={styles.headerCell}>FOB Netback</TableCell>
              <TableCell className={styles.headerCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredQuotes.map((entry, index) => {
              const isExpanded = expandedDestId === entry.destination.id;
              const isHovering =
                hoveringDestination.isHovering &&
                hoveringDestination.id === entry.destination.id;
              // Find the optimal Koch and competitor quote (cheapest valid DLVD, excluding N/O, DISC, no-price)
              const sortedQuotes = sortQuotesByNetbackAndDlvd(
                entry.quotes,
                kochPricePreference,
              );
              const cheapestKochQuote = sortedQuotes.find(
                (quote) =>
                  !quote.competitor &&
                  !!quote.freight &&
                  !quote.freight?.isExternal &&
                  resolveNetback(quote, kochPricePreference) !== null,
              );
              const cheapestCompetitorQuote = sortedQuotes.find(
                (quote) =>
                  quote.competitor &&
                  !!quote.freight &&
                  resolveNetback(quote, kochPricePreference) !== null,
              );
              const selectedNetbackQuote =
                cheapestKochQuote || cheapestCompetitorQuote;

              const {
                deliveredPrice,
                freight,
                netback,
                netbackSubText,
                priceSetter,
                competitorDeliveredPrice,
              } = resolveDisplayForQuote(
                entry.destination,
                kochPricePreference,
                selectedNetbackQuote,
                cheapestKochQuote,
                cheapestCompetitorQuote,
              );

              const { isQuoted, isNotified } = isRailFreightQuotedOrNotified(
                railTeamNotifiedRows,
                cheapestKochQuote,
              );

              // determine row key for this destination
              const rowKey = getQuoteRowKey(cheapestKochQuote);

              let freightColor = "#475569";
              if (isNotified) freightColor = "#009F5C";
              else if (isQuoted) freightColor = "#155DFC";

              let freightHoverColor = "#2563EB";
              if (isNotified) freightHoverColor = "#007A47";
              else if (isQuoted) freightHoverColor = "#1246C2";

              const freightBg = "transparent";
              const freightHoverBg = "#E8EFF7";

              return (
                <React.Fragment key={entry.destination.id}>
                  <TableRow
                    className={styles.destinationRow}
                    sx={{
                      borderTop:
                        index === 0
                          ? "2px solid rgba(190, 219, 255, 0.45)"
                          : "none",
                      "&.hover": { backgroundColor: "#f9fafb" },
                    }}
                  >
                    {/* Destination Cell */}
                    <TableCell>
                      <Button
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          paddingTop: "10px",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingBottom: "0px",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                        onMouseEnter={() =>
                          setHoveringDestination({
                            isHovering: true,
                            id: entry.destination.id,
                          })
                        }
                        onMouseLeave={() =>
                          setHoveringDestination({
                            isHovering: false,
                            id: "",
                          })
                        }
                        onClick={() =>
                          openInsightsPanel(
                            entry.destination.id,
                            "Logistics",
                            false,
                          )
                        }
                      >
                        <div
                          className={
                            isHovering
                              ? styles.destinationNameHover
                              : styles.destinationName
                          }
                        >
                          {entry.destination.displayName}
                        </div>

                        <Box className={styles.locationCell}>
                          <NitroMarkerBase
                            width={12}
                            height={20}
                            type={entry.destination.pinType || ""}
                          />
                          <span className={styles.destinationLocation}>
                            {toCamelCase(entry.destination.city || "")},{" "}
                            {entry.destination.stateOrProvince}
                          </span>
                        </Box>
                      </Button>
                    </TableCell>
                    {/* Competitor DLVD Offer Cell */}
                    <TableCell>
                      <span
                        id="competitor-dlvd-offer-header"
                        className={`${styles.destinationFOBNetback} ${
                          priceSetter === "COMPETITOR"
                            ? styles.dlvdPriceSetterText
                            : ""
                        }`}
                      >
                        ${competitorDeliveredPrice}
                      </span>
                      {cheapestCompetitorQuote &&
                        competitorDeliveredPrice !== "--" && (
                          <Box className={styles.dlvdOfferSubCell}>
                            {cheapestCompetitorQuote.mode.toUpperCase() ===
                              "TRUCK" && (
                              <TruckIconFO color="#60A5FA" paddingTop={4} />
                            )}
                            {cheapestCompetitorQuote.mode.toUpperCase() ===
                              "RAIL" && (
                              <RailIconFO color="#F97316" paddingTop={4} />
                            )}
                            <span className={styles.dlvdOfferSubText}>
                              {cheapestCompetitorQuote.origin.name}
                            </span>
                          </Box>
                        )}
                    </TableCell>
                    {/* Koch DLVD Offer Cell */}
                    <TableCell>
                      <div
                        id="dlvd-price"
                        className={`${styles.priceBold} ${
                          priceSetter === "KOCH"
                            ? styles.dlvdPriceSetterText
                            : ""
                        }`}
                      >
                        ${deliveredPrice}
                      </div>
                    </TableCell>
                    {/* Freight Cell */}
                    <TableCell>
                      {/* No Freight Value */}
                      {freight === "--" ? (
                        <span className={styles.destinationFreight}>
                          {freight}
                        </span>
                      ) : (
                        <Button
                          id="freight-button"
                          className={styles.freightCell}
                          sx={{
                            borderRadius: "3px",
                            color: freightColor,
                            backgroundColor: freightBg,
                            "&:hover": {
                              color: freightHoverColor,
                              backgroundColor: freightHoverBg,
                            },
                          }}
                          onClick={(event) => {
                            if (cheapestKochQuote?.mode === "RAIL") {
                              setRailFreightDetailContext({
                                quote: cheapestKochQuote,
                                destination: entry.destination,
                                rowKey,
                              });
                              setDisplayFreightDetailsPanel(true);
                            }
                            if (cheapestKochQuote?.mode === "TRUCK") {
                              setDisplayTruckFreightDetailsPanel(true);
                              setSelectedTruckDetailRecord(
                                cheapestKochQuote.freight as FreightTruck,
                              );
                              setAnchorForTruckDetailsPanel(
                                event.currentTarget,
                              );
                            }
                          }}
                        >
                          <span className={styles.destinationFreight}>
                            ${freight}
                          </span>
                          {isQuoted && !isNotified && <QuotedRateIcon />}
                          {isNotified && <QuotedRateNotifiedIcon />}
                        </Button>
                      )}
                    </TableCell>
                    {/* FOB Netback Cell */}
                    <TableCell>
                      <div id="fob-netback-cell" className={styles.priceBold}>
                        {selectedNetbackQuote?.mode.toUpperCase() === "RAIL" &&
                          selectedNetbackQuote.freight &&
                          "minCars" in selectedNetbackQuote.freight && (
                            <span className={styles.railCarCount}>
                              {selectedNetbackQuote.freight.minCars}+{" "}
                            </span>
                          )}
                        <span
                          id="netback-price"
                          className={styles.destinationFOBNetback}
                        >
                          {netback}
                        </span>
                      </div>
                      {selectedNetbackQuote?.freight && (
                        <Box className={styles.fobSubCell}>
                          {selectedNetbackQuote?.mode.toUpperCase() ===
                            "TRUCK" && (
                            <TruckIconFO color="#60A5FA" paddingTop={4} />
                          )}
                          {selectedNetbackQuote?.mode.toUpperCase() ===
                            "RAIL" && (
                            <RailIconFO color="#F97316" paddingTop={4} />
                          )}
                          <span className={styles.fobSubText}>
                            {selectedNetbackQuote?.origin.name} {netbackSubText}
                          </span>
                        </Box>
                      )}
                    </TableCell>
                    {/* Actions Cell */}
                    <TableCell>
                      <IconButton
                        id="destination-toggle-button-surrounding"
                        size="small"
                        disableRipple
                        onClick={() => handleToggleClick(entry.destination)}
                      >
                        {isExpanded ? (
                          <Box
                            sx={{
                              backgroundColor: "#DBEAFE",
                              borderRadius: "6px",
                              justifyContent: "center",
                              display: "flex",
                              alignItems: "center",
                              height: 24.5,
                              width: 24.5,
                            }}
                          >
                            <KeyboardArrowUp sx={{ color: "#1D4ED8" }} />
                          </Box>
                        ) : (
                          <KeyboardArrowDown />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                    <TableCell
                      colSpan={6}
                      className={styles.collapseCell}
                      sx={{ paddingRight: 0 }}
                    >
                      <Collapse
                        in={isExpanded}
                        timeout="auto"
                        unmountOnExit
                        onEntered={() => {
                          if (!autoScroll) return;
                          // auto scroll to the expanded row
                          // using double requestAnimationFrame and 300ms timeout to ensure the element is rendered before scrolling
                          requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                              setTimeout(() => {
                                autoScrollRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }, 300);
                              setAutoScroll(false);
                            });
                          });
                        }}
                      >
                        <OriginTable
                          quotes={entry.quotes}
                          destination={entry.destination}
                          resetTrigger={refetchQuotes}
                          ref={autoScrollRef}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
